import type { Runtime } from "../runtime";
import { InputBuffer, type BufferOptions, type BufferState } from "./buffer";
import { Controls, type ControlsState, type PadLike, type Vec2 } from "./controls";
import { TapeReader, TapeWriter, norm0, type InputTape } from "./tape";

/** See `Actions.snapshot`. */
export interface ActionsState {
  controls: ControlsState;
  actionHolds: [string, number][];
  actionTaps: string[];
  stickOverrides: [string, Vec2][];
  /** Toggle-mode actions switched on (absent in saves from before toggles). */
  toggled?: string[];
  /** The input buffer's slot (absent when there is no buffer, or in older saves). */
  buffer?: BufferState;
}

/** How a held action reads: "hold" (down while held) or "toggle" (each press switches it on or off; decision 0063). */
export type HoldMode = "hold" | "toggle";

// Action maps: gameplay reads named actions ("jump", "move", "look"), never devices. A map is plain data,
// so a composition's controls are one table, and rebinding is a data edit. Three kinds of action:
//
//   buttons  digital (or analogue 0..1): any bound control
//   sticks   a 2D direction in the unit disc: four digital directions plus analogue vectors (pad, touch)
//   deltas   accumulated 2D motion consumed once per step: pointer pixels × scale plus a stick × rate × dt
//
// Injected input for tests goes through the same table (`controls.hold("KeyW", s)`) or straight to an action
// (`actions.holdAction("jump", s)`, `actions.setStick("move", x, y)`), all on sim time.
//
// Contexts and modes: `setActive` makes actions outside the active input contexts read idle (contexts.ts drives it);
// `setHoldMode(action, "toggle")` turns a held action into a switch; `buffer` (options) keeps the universal input
// buffer (buffer.ts), stepped at the start of every sim step.
//
// Recording and replay (tape.ts): `record()` writes down what each sim step read, `replay(tape)` feeds it back through
// the same reads. Only reads inside a step (between beginStep and endStep) are recorded or replayed; a read outside a
// step (a prompt, a HUD) always sees the live state.

export interface StickBinding {
  up?: string[];
  down?: string[];
  left?: string[];
  right?: string[];
  /** Analogue vector controls ("PadLeft", "Touch:move"), already dead-zoned. */
  vectors?: string[];
}

export interface DeltaBinding {
  /** Accumulated deltas ("PointerDrag", "Touch:look") and their scale per axis. */
  pointer?: { control: string; scale: [number, number] }[];
  /** A stick turned into a rate (per second) and integrated over the step's dt. */
  stick?: StickBinding & { rate: [number, number] };
}

export interface ActionMap {
  buttons?: Record<string, string[]>;
  sticks?: Record<string, StickBinding>;
  deltas?: Record<string, DeltaBinding>;
}

export class Actions {
  readonly controls: Controls;
  private map: Required<ActionMap>;
  private readonly now: () => number;
  private actionHolds = new Map<string, number>();
  private actionTaps = new Set<string>();
  private actionStepTaps = new Set<string>();
  private stickOverrides = new Map<string, Vec2>();
  private inStep = false;
  private stepIndex = -1;
  private writer: TapeWriter | null = null;
  private reader: TapeReader | null = null;
  private active: ((action: string) => boolean) | null = null;
  private toggleMode = new Set<string>();
  private toggled = new Set<string>();
  private readonly stepDt: number;
  /** The universal input buffer, when the options asked for one (stepped in `beginStep`). */
  readonly buffer: InputBuffer | null;

  /** `now` is sim time in seconds; `buffer` adds the input buffer; `dt` is the step size it ages by when
   *  `beginStep` isn't given one (`bindActions` passes the runtime's). */
  constructor(map: ActionMap, o: { controls?: Controls; now?: () => number; buffer?: BufferOptions; dt?: number } = {}) {
    this.now = o.now ?? (() => 0);
    this.controls = o.controls ?? new Controls(this.now);
    this.map = { buttons: map.buttons ?? {}, sticks: map.sticks ?? {}, deltas: map.deltas ?? {} };
    this.buffer = o.buffer ? new InputBuffer(o.buffer) : null;
    this.stepDt = o.dt ?? 1 / 60;
  }

  /** Replace (part of) the map: rebinding is a data edit. */
  rebind(map: ActionMap): void {
    this.map = {
      buttons: { ...this.map.buttons, ...map.buttons },
      sticks: { ...this.map.sticks, ...map.sticks },
      deltas: { ...this.map.deltas, ...map.deltas },
    };
  }

  /** Replace the whole map (contexts and the rebinding model compile a fresh one). */
  setMap(map: ActionMap): void {
    this.map = { buttons: map.buttons ?? {}, sticks: map.sticks ?? {}, deltas: map.deltas ?? {} };
  }

  /** Only actions `filter` passes read live; the rest read idle (no value, press, stick or delta, injected input
   *  included). Null makes every action active again. */
  setActive(filter: ((action: string) => boolean) | null): void {
    this.active = filter;
  }

  /** Whether an action reads live now (see `setActive`). */
  isActive(action: string): boolean {
    return this.active === null || this.active(action);
  }

  /** Hold or toggle for a button action. Switching back to hold switches the toggle off. */
  setHoldMode(action: string, mode: HoldMode): void {
    if (mode === "toggle") this.toggleMode.add(action);
    else {
      this.toggleMode.delete(action);
      this.toggled.delete(action);
    }
  }

  /** The action's hold mode ("hold" unless set). */
  holdMode(action: string): HoldMode {
    return this.toggleMode.has(action) ? "toggle" : "hold";
  }

  /** Switch every toggled action off (a death, a cutscene, a context that should drop them). */
  clearToggles(): void {
    this.toggled.clear();
  }

  bindingsOf(action: string): string[] {
    return this.map.buttons[action] ?? [];
  }

  // ── buttons ──────────────────────────────────────────────────────────────────────────────────────

  /** Analogue value 0..1 (the strongest bound control). */
  value(action: string): number {
    if (this.playing()) return this.reader!.value(action);
    const v = this.liveValue(action);
    if (this.writer && this.inStep) this.writer.level(`v:${action}`, v);
    return v;
  }

  down(action: string): boolean {
    return this.value(action) > 0.5;
  }

  /** A value from outside the input that decides the sim this step (e.g. the camera's yaw the stick is relative to):
   *  `live()` while playing or recording (written to the tape as "g:<key>"), the recorded value in a replay, so a
   *  replay takes the same path whatever the outside thing does now. Read it inside a sim step. */
  sample(key: string, live: () => number): number {
    if (this.playing()) return this.reader!.sampled(key);
    const v = norm0(live());
    if (this.writer && this.inStep) this.writer.level(`g:${key}`, v);
    return v;
  }

  /** Pressed since the previous step: true in exactly one sim step per press. */
  pressed(action: string): boolean {
    if (this.playing()) return this.reader!.pressed(action);
    const on = this.isActive(action) && this.livePressed(action);
    if (this.writer && this.inStep) this.writer.press(`p:${action}`, on);
    return on;
  }

  // ── sticks ───────────────────────────────────────────────────────────────────────────────────────

  /** A direction in the unit disc: x right, y forward/up. */
  stick(action: string): Vec2 {
    if (this.playing()) return this.reader!.stick(action);
    const o = this.stickOverrides.get(action);
    const b = this.map.sticks[action];
    const s = !this.isActive(action) ? { x: 0, y: 0 } : o ? { x: o.x, y: o.y } : b ? this.readStick(b) : { x: 0, y: 0 };
    if (this.writer && this.inStep) {
      s.x = norm0(s.x);
      s.y = norm0(s.y);
      this.writer.level(`s:${action}`, [s.x, s.y]);
    }
    return s;
  }

  // ── deltas ───────────────────────────────────────────────────────────────────────────────────────

  /** Consume the accumulated delta for this action; `dt` integrates its stick rate. */
  consumeDelta(action: string, dt: number): Vec2 {
    const live = this.liveDelta(action, dt); // consumed even in replay, so live motion doesn't pile up behind a tape
    if (this.playing()) return this.reader!.delta(action);
    if (!this.isActive(action)) live.x = live.y = 0;
    if (this.writer && this.inStep) {
      live.x = norm0(live.x);
      live.y = norm0(live.y);
      this.writer.delta(`d:${action}`, live.x, live.y);
    }
    return live;
  }

  private liveDelta(action: string, dt: number): Vec2 {
    const b = this.map.deltas[action];
    if (!b) return { x: 0, y: 0 };
    let x = 0;
    let y = 0;
    for (const p of b.pointer ?? []) {
      const d = this.controls.takeDelta(p.control);
      x += d.x * p.scale[0];
      y += d.y * p.scale[1];
    }
    if (b.stick) {
      const s = this.readStick(b.stick);
      x += s.x * b.stick.rate[0] * dt;
      y += s.y * b.stick.rate[1] * dt;
    }
    return { x, y };
  }

  // ── injection (tests, bots, cutscenes) ───────────────────────────────────────────────────────────

  /** Hold an action for `seconds` of sim time; counts as a press too. */
  holdAction(action: string, seconds: number): void {
    this.actionHolds.set(action, this.now() + seconds);
    this.actionTaps.add(action);
  }

  /** One press edge with no hold. */
  tapAction(action: string): void {
    this.actionTaps.add(action);
  }

  /** Override a stick (a bot steering); null clears it. */
  setStick(action: string, v: Vec2 | null): void {
    if (v) this.stickOverrides.set(action, v);
    else this.stickOverrides.delete(action);
  }

  // ── step boundaries ──────────────────────────────────────────────────────────────────────────────

  /** A sim step starts. `step` is its absolute index (the runtime clock's step; `bindActions` passes it); without it
   *  the count goes on from the previous step. Recording and replay key on it. */
  beginStep(step?: number, dt?: number): void {
    this.stepIndex = step ?? this.stepIndex + 1;
    this.controls.beginStep();
    for (const a of this.actionTaps) this.actionStepTaps.add(a);
    this.actionTaps.clear();
    this.inStep = true;
    this.writer?.begin(this.stepIndex);
    if (this.reader?.covers(this.stepIndex)) this.reader.seek(this.stepIndex);
    // a toggle flips on its press edge (live; in replay its recorded value is read instead)
    for (const a of this.toggleMode)
      if (this.isActive(a) && this.livePressed(a)) {
        if (this.toggled.has(a)) this.toggled.delete(a);
        else this.toggled.add(a);
      }
    this.buffer?.step(dt ?? this.stepDt, (a) => this.pressed(a));
  }

  endStep(): void {
    this.controls.endStep();
    this.actionStepTaps.clear();
    this.inStep = false;
  }

  // ── recording and replay ─────────────────────────────────────────────────────────────────────────

  /** Start recording what each step reads, from the next step on (a recording already running is restarted). */
  record(): void {
    this.writer = new TapeWriter();
  }

  /** Whether a recording is running. */
  get recording(): boolean {
    return this.writer !== null;
  }

  /** Stop recording and return the tape (null if none was running). The step running now, if any, counts as recorded. */
  stopRecording(): InputTape | null {
    const t = this.writer?.finish() ?? null;
    this.writer = null;
    return t;
  }

  /** Replay a tape: inside its steps every read returns the recorded value, and live devices, injected input and
   *  stick overrides are ignored; outside them, reads are live again. Keyed by absolute step, so it works from any
   *  step inside the tape (a restored save). Throws if `tape` is not an input tape. */
  replay(tape: InputTape): void {
    this.reader = new TapeReader(tape);
    if (this.inStep && this.reader.covers(this.stepIndex)) this.reader.seek(this.stepIndex);
  }

  /** Stop replaying; reads are live again. */
  stopReplay(): void {
    this.reader = null;
  }

  /** Whether a tape is set and the current (or next) step is inside it. */
  get replaying(): boolean {
    const r = this.reader;
    return r !== null && r.covers(this.inStep ? this.stepIndex : this.stepIndex + 1);
  }

  /** The injected, sim-time input state (control holds and latched presses, action holds and taps, stick
   *  overrides), for a save or a replay checkpoint. Live device state is not included (see Controls.snapshot). */
  snapshot(): ActionsState {
    return {
      controls: this.controls.snapshot(),
      actionHolds: [...this.actionHolds.entries()],
      actionTaps: [...this.actionTaps],
      stickOverrides: [...this.stickOverrides.entries()].map(([k, v]) => [k, { x: v.x, y: v.y }]),
      // (left out when empty, so a save without toggles or a buffered press reads as one from before they existed)
      ...(this.toggled.size ? { toggled: [...this.toggled] } : {}),
      ...(this.buffer?.action != null ? { buffer: this.buffer.snapshot() } : {}),
    };
  }

  restore(s: ActionsState): void {
    this.controls.restore(s.controls);
    this.actionHolds = new Map(s.actionHolds);
    this.actionTaps = new Set(s.actionTaps);
    this.actionStepTaps.clear();
    this.stickOverrides = new Map(s.stickOverrides.map(([k, v]) => [k, { x: v.x, y: v.y }]));
    this.toggled = new Set(s.toggled ?? []);
    this.buffer?.restore(s.buffer);
  }

  /** Every mapped action's state (for a harness). */
  describe(): { buttons: Record<string, boolean>; sticks: Record<string, Vec2>; controls: ReturnType<Controls["describe"]> } {
    const round = (v: Vec2): Vec2 => ({ x: Math.round(v.x * 1000) / 1000, y: Math.round(v.y * 1000) / 1000 });
    return {
      buttons: Object.fromEntries(Object.keys(this.map.buttons).map((a) => [a, this.down(a)])),
      sticks: Object.fromEntries(Object.keys(this.map.sticks).map((a) => [a, round(this.stick(a))])),
      controls: this.controls.describe(),
    };
  }

  private playing(): boolean {
    return this.inStep && this.reader !== null && this.reader.covers(this.stepIndex);
  }

  private livePressed(action: string): boolean {
    return this.actionStepTaps.has(action) || this.bindingsOf(action).some((c) => this.controls.pressed(c));
  }

  private liveValue(action: string): number {
    if (!this.isActive(action)) return 0;
    if (this.toggleMode.has(action)) return this.toggled.has(action) ? 1 : 0;
    if (this.heldByInjection(action)) return 1;
    let v = 0;
    for (const c of this.bindingsOf(action)) v = Math.max(v, this.controls.value(c));
    return v;
  }

  private heldByInjection(action: string): boolean {
    const until = this.actionHolds.get(action);
    if (until === undefined) return false;
    if (this.now() < until) return true;
    this.actionHolds.delete(action);
    return false;
  }

  private readStick(b: StickBinding): Vec2 {
    const any = (cs?: string[]): number => Math.max(0, ...(cs ?? []).map((c) => this.controls.value(c)));
    let x = any(b.right) - any(b.left);
    let y = any(b.up) - any(b.down);
    for (const v of b.vectors ?? []) {
      const s = this.controls.vector(v);
      if (Math.hypot(s.x, s.y) > Math.hypot(x, y)) {
        x = s.x;
        y = s.y;
      }
    }
    const m = Math.hypot(x, y);
    if (m > 1) {
      x /= m;
      y /= m;
    }
    return { x, y };
  }
}

/** Register an Actions object's step boundaries and pad polling with a runtime-shaped scheduler. */
export function bindActions(rt: Pick<Runtime, "on">, actions: Actions, o: { pads?: () => readonly (PadLike | null)[] } = {}): () => void {
  const offs = [
    rt.on("sim", (c) => actions.beginStep(c.step, c.dt), { order: -1e9, name: "input:beginStep" }),
    rt.on("sim", () => actions.endStep(), { order: 1e9, name: "input:endStep" }),
  ];
  const pads = o.pads;
  if (pads) offs.push(rt.on("input", () => actions.controls.pollPads(pads()), { order: -1e9, name: "input:pads" }));
  return () => offs.forEach((f) => f());
}

/** The browser's gamepads, or none (no Gamepad API, or not allowed). */
export function browserPads(): readonly (PadLike | null)[] {
  try {
    return typeof navigator !== "undefined" && navigator.getGamepads ? navigator.getGamepads() : [];
  } catch {
    return [];
  }
}

